import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { useMember } from "../contexts/MemberContext";

// Handles the Emergent Auth redirect: exchanges the short-lived session_id
// (delivered in the URL hash) for a persistent member session, then routes
// to the member portal. hasProcessed guards against double-exchange under
// React StrictMode's double-invoked effects.
export default function MemberAuthCallback() {
  const { exchangeSession } = useMember();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      navigate("/member", { replace: true });
      return;
    }

    exchangeSession(sessionId).then((result) => {
      if (result?.ok) {
        navigate("/member", { replace: true });
        return;
      }
      setError(result?.error || "Could not complete Google sign-in.");
    });
  }, [exchangeSession, navigate]);

  if (error) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="member-auth-callback-error">
        <div className="card-dark max-w-lg w-full p-8 text-center">
          <AlertTriangle className="w-8 h-8 text-[var(--red)] mx-auto mb-4" />
          <h1 className="font-display text-2xl font-semibold tracking-tight mb-3">Google sign-in could not be completed</h1>
          <p className="text-sm text-[var(--text-2)] mb-6">{error}</p>
          <Link to="/member" className="btn-red" data-testid="member-auth-callback-return">
            Back to Member Portal
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="member-auth-callback">
      <Loader2 className="w-6 h-6 text-[var(--red)] animate-spin" />
    </div>
  );
}
